// Bikram Sambat (BS) conversion from the month-length table in calendar/bs-months.csv
// (ADR-0022). It mirrors backend/pkg/bs, so a printed ticket and the screen agree.

import { epochDay, maxYear, minYear, monthDays, provisionalFrom } from "./bs-table.gen";
import { toNepaliDigits, type Lang } from "./money";

/** A day in the BS calendar. Month runs from 1 (Baisakh) to 12 (Chaitra). */
export type BsDate = { year: number; month: number; day: number };

const dayMs = 86_400_000;
// Nepal is UTC+5:45 all year.
const nepalOffsetMs = (5 * 60 + 45) * 60 * 1000;

const monthNames: Record<Lang, readonly string[]> = {
  en: ["Baisakh", "Jestha", "Asar", "Shrawan", "Bhadra", "Asoj", "Kartik", "Mangsir", "Poush", "Magh", "Falgun", "Chaitra"],
  ne: ["वैशाख", "जेठ", "असार", "साउन", "भदौ", "असोज", "कात्तिक", "मंसिर", "पुस", "माघ", "फागुन", "चैत"],
};

const pad = (n: number, width: number) => String(n).padStart(width, "0");

/** Writes d as "2083-07-03". */
export function bsIso(d: BsDate): string {
  return `${pad(d.year, 4)}-${pad(d.month, 2)}-${pad(d.day, 2)}`;
}

/**
 * Returns the BS date of an instant's calendar day in Nepal, whatever the device's
 * time zone. Throws a RangeError outside the table.
 */
export function fromAd(instant: Date | number): BsDate {
  const ms = typeof instant === "number" ? instant : instant.getTime();
  let n = Math.floor((ms + nepalOffsetMs) / dayMs) - epochDay;
  if (n >= 0) {
    for (let i = 0; i < monthDays.length; i++) {
      for (let j = 0; j < 12; j++) {
        if (n < monthDays[i][j]) return { year: minYear + i, month: j + 1, day: n + 1 };
        n -= monthDays[i][j];
      }
    }
  }

  throw new RangeError(`no BS date for ${new Date(ms + nepalOffsetMs).toISOString().slice(0, 10)}: the calendar table covers ${minYear}–${maxYear}`);
}

/** Returns the AD date of d as midnight UTC in ms. Throws a RangeError for a date not in the table. */
export function toAd(d: BsDate): number {
  if (d.year < minYear || d.year > maxYear) {
    throw new RangeError(`BS ${bsIso(d)} is outside the calendar table, ${minYear}–${maxYear}`);
  }
  const months = monthDays[d.year - minYear];
  if (!(d.month >= 1 && d.month <= 12 && d.day >= 1 && d.day <= months[d.month - 1])) {
    throw new RangeError(`no such BS date: ${bsIso(d)}`);
  }

  let n = epochDay + d.day - 1;
  for (const year of monthDays.slice(0, d.year - minYear)) n += year.reduce((a, b) => a + b, 0);
  for (const length of months.slice(0, d.month - 1)) n += length;

  return n * dayMs;
}

/** Whether a BS year's month lengths are a projection, not yet checked against the approved calendar. */
export function isProvisional(year: number): boolean {
  return year >= provisionalFrom;
}

/** The name of month 1 (Baisakh) to 12 (Chaitra). */
export function bsMonthName(month: number, lang: Lang): string {
  return monthNames[lang][month - 1] ?? "";
}

/** Formats d as "3 Kartik 2083", or "३ कात्तिक २०८३" in Nepali. */
export function formatBs(d: BsDate, lang: Lang): string {
  const s = `${d.day} ${bsMonthName(d.month, lang)} ${d.year}`;

  return lang === "ne" ? toNepaliDigits(s) : s;
}
